import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Edit, Trash2, User, Calendar, AlertCircle, CheckCircle, XCircle, Droplet, Hospital, Phone } from 'lucide-react';
import { toast } from 'react-hot-toast';

const RequestDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchRequestDetails(id);
    } else {
      setError('Request ID is missing');
      setLoading(false);
    }
  }, [id]);

  const fetchRequestDetails = async (requestId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/requests/${requestId}`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      
      setRequest(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching request details:', err);
      setError('Failed to fetch request details. Please try again.');
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/dashboard/requests/form/${id}`);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/requests/${id}`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      
      toast.success('Request deleted successfully');
      setShowDeleteModal(false);
      navigate('/admin/dashboard/requests');
    } catch (err) {
      console.error('Error deleting request:', err);
      setError('Failed to delete request. Please try again.');
      setLoading(false);
      setShowDeleteModal(false);
      toast.error('Error deleting request');
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/requests/${id}/status`,
        {
          status: newStatus
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      
      // Refresh request details
      fetchRequestDetails(id);
      toast.success(`Request status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating request status:', err);
      setError('Failed to update request status. Please try again.');
      setLoading(false);
      toast.error('Error updating request status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'fulfilled':
        return 'bg-green-100 text-green-800';
      case 'partially_fulfilled':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyBadgeColor = (urgency) => {
    switch (urgency) {
      case 'emergency':
        return 'bg-red-100 text-red-800';
      case 'urgent':
        return 'bg-orange-100 text-orange-800';
      case 'normal':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Notice:</strong>
        <span className="block sm:inline"> Request not found</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/admin/dashboard/requests')}
            className="mr-4 text-gray-600 hover:text-gray-800 flex items-center"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
          <h2 className="text-2xl font-semibold text-gray-800">
            Blood Request Details
          </h2>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleEdit}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </button>
        </div>
      </div>

      {/* Status and Actions */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center mb-2 md:mb-0">
            <span className="font-medium text-gray-700 mr-2">Status:</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(request.status)}`}>
              {request.status?.charAt(0).toUpperCase() + request.status?.slice(1).replace('_', ' ')}
            </span>
          </div>
          <div className="flex space-x-2">
            {request.status !== 'fulfilled' && (
              <button
                onClick={() => handleStatusUpdate('fulfilled')}
                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center text-sm"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark as Fulfilled
              </button>
            )}
            {request.status !== 'partially_fulfilled' && (
              <button
                onClick={() => handleStatusUpdate('partially_fulfilled')}
                className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center text-sm"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark as Partially Fulfilled
              </button>
            )}
            {request.status !== 'cancelled' && (
              <button
                onClick={() => handleStatusUpdate('cancelled')}
                className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center text-sm"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Cancel Request
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Patient Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-red-500" />
            Patient Information
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Patient Name:</span>
              <span className="font-medium text-gray-900">{request.patientName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Blood Type:</span>
              <span className="font-medium text-gray-900 flex items-center">
                <Droplet className="w-4 h-4 mr-1 text-red-500" />
                {request.bloodType}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Units Needed:</span>
              <span className="font-medium text-gray-900">{request.unitsNeeded || 1}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Urgency:</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getUrgencyBadgeColor(request.urgency)}`}>
                {request.urgency?.charAt(0).toUpperCase() + request.urgency?.slice(1)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Reason:</span>
              <span className="font-medium text-gray-900 text-right max-w-[60%]">{request.reason}</span>
            </div>
          </div>
        </div>

        {/* Hospital Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Hospital className="w-5 h-5 mr-2 text-red-500" />
            Hospital Information
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Hospital:</span>
              <span className="font-medium text-gray-900">{request.hospital?.name || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Address:</span>
              <span className="font-medium text-gray-900 text-right max-w-[60%]">
                {request.hospital?.address || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">City:</span>
              <span className="font-medium text-gray-900">{request.hospital?.city || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">State:</span>
              <span className="font-medium text-gray-900">{request.hospital?.state || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Phone:</span>
              <span className="font-medium text-gray-900">{request.hospital?.phone || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Dates and Timeline */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-red-500" />
            Dates and Timeline
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Created On:</span>
              <span className="font-medium text-gray-900">{formatDateTime(request.createdAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Required By:</span>
              <span className="font-medium text-gray-900">{formatDate(request.requiredBy)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last Updated:</span>
              <span className="font-medium text-gray-900">{formatDateTime(request.updatedAt)}</span>
            </div>
            {request.statusUpdatedAt && (
              <div className="flex justify-between">
                <span className="text-gray-600">Status Updated:</span>
                <span className="font-medium text-gray-900">{formatDateTime(request.statusUpdatedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Phone className="w-5 h-5 mr-2 text-red-500" />
            Contact Information
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Contact Name:</span>
              <span className="font-medium text-gray-900">{request.contactInfo?.name || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Relationship:</span>
              <span className="font-medium text-gray-900">{request.contactInfo?.relationship || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Phone:</span>
              <span className="font-medium text-gray-900">{request.contactInfo?.phone || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-medium text-gray-900">{request.contactInfo?.email || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Notes */}
      {request.adminNotes && (
        <div className="bg-gray-50 p-4 rounded-lg mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
            Admin Notes
          </h3>
          <p className="text-gray-700 whitespace-pre-line">{request.adminNotes}</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this blood request? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestDetails;